import { useState } from 'react';
import { Calendar } from './components/Calendar';
import CreateNewRotaModal from './components/CreateNewRotaModal';
import moment, { Moment } from 'moment';
import { useGetRotas } from './hooks';
import { CALENDAR_MODE__MONTH } from '../../constants';
import { Button } from '../../ui/Button';
import { Container } from '../../ui/Container';
import { Header, HeaderTitle, HeaderAction } from '../../ui/Header';
import { UserGroupIcon } from '@heroicons/react/20/solid';
import { ShiftDetail } from './components/ShiftDetail';
import { EmployeeScreen } from './components/EmployeeScreen';
import { CalendarSkeleton } from './components/CalendarSkeleton';
import { Link } from 'react-router-dom';
import rota from '../../network/rota';
import { useToast } from '../../utils/useToast';

export function RotaManagementRoute() {
  const [isOpenNewRotaModal, setIsOpenNewRotaModal] = useState(false);
  const [today, setToday] = useState(moment());
  const [mode, setMode] = useState(CALENDAR_MODE__MONTH);
  const [selectedShift, setSelectedShift] = useState<any>(null);
  const [isShiftDetailOpen, setIsShiftDetailOpen] = useState(false);
  const [showEmployeeScreen, setShowEmployeeScreen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const { toast } = useToast();

  const startDay =
    mode === CALENDAR_MODE__MONTH
      ? today.clone().startOf('month').startOf('week')
      : today.clone().startOf('week');

  const daysCount = mode === CALENDAR_MODE__MONTH ? 42 : 7;

  const daysMap: Moment[] = [...Array(daysCount)].map((_, index) =>
    startDay.clone().add(index, 'day'),
  );

  // Keyed by the visible range, so moving between weeks or months loads that
  // range instead of showing the last one.
  const {
    data: rotasResponse = [],
    refetch: refetchRotas,
    isLoading,
    isError,
  } = useGetRotas(daysMap);
  const rotas = rotasResponse.flatMap((response) => response?.data || []);

  function handlePrevious() {
    setToday((prev) =>
      prev
        .clone()
        .subtract(1, mode === CALENDAR_MODE__MONTH ? 'month' : 'week'),
    );
  }

  function handleToday() {
    setToday(moment());
  }

  function handleNext() {
    setToday((prev) =>
      prev.clone().add(1, mode === CALENDAR_MODE__MONTH ? 'month' : 'week'),
    );
  }

  /**
   * Publishes every unpublished schedule in the visible range. The API
   * notifies the employees on each one.
   */
  async function handlePublish() {
    const unpublished = Array.from(
      new Map(
        rotas
          .filter((item: any) => item?.id && !item?.published)
          .map((item: any) => [item.id, item]),
      ).values(),
    );

    if (!unpublished.length) {
      toast({
        title: 'Nothing to publish',
        description: 'Every shift in this period is already published.',
        variant: 'info',
      });
      return;
    }

    const confirmed = window.confirm(
      `Publish ${unpublished.length} schedule(s) in this period? Assigned employees will be notified.`,
    );
    if (!confirmed) return;

    setIsPublishing(true);
    const results = await Promise.allSettled(
      unpublished.map((item: any) => rota.publishSchedule(item.id)),
    );
    setIsPublishing(false);

    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length === 0) {
      toast({
        title: 'Shifts published',
        description: `${unpublished.length} schedule(s) published and employees notified.`,
        variant: 'success',
      });
    } else {
      const reason = (failed[0] as PromiseRejectedResult).reason;
      toast({
        title:
          failed.length === unpublished.length
            ? 'Shifts not published'
            : `${unpublished.length - failed.length} of ${unpublished.length} schedules published`,
        description:
          reason?.response?.data?.message ||
          'Some schedules could not be published. Please try again.',
        variant: 'error',
      });
    }
    await refetchRotas();
  }

  function handleRotaClick(rota: any) {
    setSelectedShift(rota);
    setIsShiftDetailOpen(true);
  }

  function handleEditShift(shiftId: string) {
    // For now, just close the modal. In a real app, you'd open the edit form.
    setIsShiftDetailOpen(false);
    console.log('Edit shift:', shiftId);
    // Here you would typically open the edit modal with the shift data pre-filled
  }

  function handleDeleteShift(shiftId: string) {
    setIsShiftDetailOpen(false);
    console.log('Delete shift:', shiftId);
    // Here you would typically show a confirmation dialog and then delete the shift
  }

  function handleViewEmployeeDetail(employeeId: string) {
    console.log('View employee detail:', employeeId);
    // Here you would typically navigate to the employee detail page or open a modal
  }

  if (showEmployeeScreen) {
    return (
      <EmployeeScreen
        rotas={rotas}
        onViewEmployeeDetail={handleViewEmployeeDetail}
        onBack={() => setShowEmployeeScreen(false)}
      />
    );
  }

  return (
    <>
      <Container>
        <Header className="mb-8">
          <HeaderTitle>Rota Management</HeaderTitle>
          <HeaderAction>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEmployeeScreen(true)}
                className="gap-1.5"
              >
                <UserGroupIcon className="h-4 w-4" />
                Manage Employees
              </Button>
              <Link
                to="/my-shifts"
                className="text-sm font-medium text-slate-700 hover:text-red-600"
              >
                My shifts
              </Link>
              <Button
                color="red"
                onClick={handlePublish}
                disabled={isPublishing || isLoading}
              >
                {isPublishing ? 'Publishing...' : 'Publish shifts'}
              </Button>
            </div>
          </HeaderAction>
        </Header>

        {isError && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>We could not load the rota for this period.</span>
            <Button variant="outline" onClick={() => refetchRotas()}>
              Try again
            </Button>
          </div>
        )}

        {isLoading ? (
          <CalendarSkeleton />
        ) : (
          <Calendar
            today={today}
            daysMap={daysMap}
            rotas={rotas}
            setIsOpenNewRotaModal={setIsOpenNewRotaModal}
            handlePrevious={handlePrevious}
            handleToday={handleToday}
            handleNext={handleNext}
            setMode={setMode}
            mode={mode}
            onRotaClick={handleRotaClick}
          />
        )}
      </Container>

      <CreateNewRotaModal
        open={isOpenNewRotaModal}
        setOpen={setIsOpenNewRotaModal}
        refetchRotas={refetchRotas}
      />

      {selectedShift && (
        <ShiftDetail
          shift={selectedShift}
          open={isShiftDetailOpen}
          onClose={() => setIsShiftDetailOpen(false)}
          onEdit={handleEditShift}
          onDelete={handleDeleteShift}
          refetchRotas={refetchRotas}
        />
      )}
    </>
  );
}
